/* Scoot Motion: persist the customer's CGV action before enabling cart checkout. */
(() => {
  'use strict';
  if (customElements.get('terms-checkbox')) return;

  const KEYS = {
    accepted: 'CGV — Acceptation',
    version: 'CGV — Version',
    timestamp: 'CGV — Acceptées le (heure du navigateur)',
    language: 'CGV — Langue',
    label: 'CGV — Libellé accepté',
    url: 'CGV — URL',
    hash: 'CGV — Empreinte SHA-256',
    source: 'CGV — Mode d’acceptation'
  };
  const emptyAttributes = () => Object.fromEntries(Object.values(KEYS).map(key => [key, '']));
  const state = {
    widgets: new Set(), started: false, revision: 0,
    desired: { revision: 0, accepted: false, attributes: emptyAttributes(), policy: null },
    savedRevision: -1, savedAccepted: false, running: false,
    cartCheckId: 0, checkingCart: false, error: ''
  };
  const controls = () => document.querySelectorAll('#CheckOut, #cart [name="checkout"], [form="cart"][name="checkout"]');
  const canCheckout = () => state.desired.accepted && state.savedAccepted &&
    state.savedRevision === state.desired.revision && !state.running && !state.checkingCart;
  const cartRoute = name => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + name;
  };

  function policyFor(widget) {
    const label = widget.querySelector('label');
    const policy = {
      version: (widget.dataset.cgvVersion || '').trim(),
      language: (widget.dataset.cgvLanguage || '').trim(),
      label: label ? (label.innerText || label.textContent || '').trim().replace(/\s+/g, ' ') : '',
      url: (widget.dataset.cgvUrl || '').trim(),
      hash: (widget.dataset.cgvHash || '').trim().toLowerCase()
    };
    if (!policy.version || !policy.language || !policy.label || !/^[a-f0-9]{64}$/.test(policy.hash)) {
      throw new Error('Incomplete CGV configuration');
    }
    const url = new URL(policy.url, document.baseURI);
    if (!policy.url || !['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid CGV URL');
    policy.url = url.href;
    return policy;
  }
  function samePolicy(a, b) {
    return !!a && !!b && Object.keys(a).every(key => a[key] === b[key]);
  }
  function render() {
    const allowed = canCheckout();
    for (const control of controls()) {
      if ('disabled' in control) control.disabled = !allowed;
      control.setAttribute('aria-disabled', String(!allowed));
    }
    for (const widget of state.widgets) {
      widget.checkbox.checked = state.desired.accepted;
      widget.setAttribute('aria-busy', String(state.running || state.checkingCart));
      widget.errorOutput.hidden = !state.error;
      if (widget.errorOutput.textContent !== state.error) widget.errorOutput.textContent = state.error;
    }
  }
  function change(accepted, widget, keepError = false) {
    let policy = null;
    let attributes = emptyAttributes();
    if (!keepError) state.error = '';
    if (accepted) {
      try {
        policy = policyFor(widget);
        attributes = {
          [KEYS.accepted]: 'Oui — case cochée par le client',
          [KEYS.version]: policy.version,
          [KEYS.timestamp]: new Date().toISOString(),
          [KEYS.language]: policy.language,
          [KEYS.label]: policy.label,
          [KEYS.url]: policy.url,
          [KEYS.hash]: policy.hash,
          [KEYS.source]: 'Case CGV du panier — thème Scoot Motion'
        };
      } catch (error) {
        accepted = false;
        state.error = 'L’acceptation des CGV ne peut pas être enregistrée. Veuillez réessayer ou contacter la boutique.';
      }
    }
    state.revision += 1;
    state.cartCheckId += 1;
    state.checkingCart = false;
    state.desired = { revision: state.revision, accepted, attributes, policy };
    render();
    void flush();
  }
  async function updateCart(attributes) {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 15000);
    try {
      const response = await fetch(cartRoute('cart/update.js'), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }), signal: abort.signal
      });
      if (!response.ok) throw new Error('Cart update failed: ' + response.status);
      const cart = await response.json();
      if (!cart || !cart.attributes || !Object.entries(attributes).every(([key, value]) =>
        value === '' ? !cart.attributes[key] : cart.attributes[key] === value)) {
        throw new Error('Cart did not retain the CGV attributes');
      }
      return cart;
    } finally { clearTimeout(timeout); }
  }
  async function checkCartAfterUpdate() {
    if (!state.started || !state.desired.accepted) return;
    const revision = state.desired.revision;
    const checkId = ++state.cartCheckId;
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 15000);
    state.checkingCart = true;
    render();
    try {
      const response = await fetch(cartRoute('cart.js'), {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: abort.signal
      });
      if (!response.ok) throw new Error('Cart verification failed');
      const cart = await response.json();
      if (!cart || typeof cart.item_count !== 'number') throw new Error('Invalid cart response');
      if (checkId === state.cartCheckId && revision === state.desired.revision) {
        if (cart.item_count === 0) change(false, null);
        else state.error = '';
      }
    } catch (error) {
      if (checkId === state.cartCheckId && revision === state.desired.revision) {
        state.error = 'Impossible de vérifier l’acceptation des CGV. Veuillez cocher à nouveau la case pour réessayer.';
        change(false, null, true);
      }
    } finally {
      clearTimeout(timeout);
      if (checkId === state.cartCheckId) { state.checkingCart = false; render(); }
    }
  }
  async function flush() {
    if (state.running) return;
    state.running = true;
    render();
    try {
      while (state.savedRevision !== state.desired.revision) {
        const snapshot = state.desired;
        try {
          const cart = await updateCart(snapshot.attributes);
          state.savedRevision = snapshot.revision;
          state.savedAccepted = snapshot.accepted;
          if (snapshot.accepted && snapshot.revision === state.desired.revision) state.error = '';
          if (snapshot.accepted && cart.item_count === 0 && snapshot.revision === state.desired.revision) change(false, null);
        } catch (error) {
          state.savedAccepted = false;
          if (snapshot.revision === state.desired.revision) {
            state.error = 'Impossible d’enregistrer l’acceptation des CGV. Veuillez cocher à nouveau la case pour réessayer.';
            if (snapshot.accepted) change(false, null, true);
            else break;
          }
        }
        render();
      }
    } finally { state.running = false; render(); }
  }
  function isCartCheckout(target) {
    return [...controls()].some(control => control === target || control.contains(target));
  }
  function blockCheckout(event) {
    if (canCheckout()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!state.error) state.error = state.running || state.checkingCart
      ? 'L’acceptation des CGV est en cours d’enregistrement. Veuillez patienter.'
      : 'Veuillez accepter les CGV avant de passer au paiement.';
    render();
  }
  document.addEventListener('click', event => {
    if (state.started && isCartCheckout(event.target)) blockCheckout(event);
  }, true);
  document.addEventListener('cart-updated', () => { void checkCartAfterUpdate(); }, true);
  document.addEventListener('submit', event => {
    if (!state.started) return;
    const submitter = event.submitter;
    const formHasCheckout = [...controls()].some(control => control.form === event.target);
    if ((submitter && isCartCheckout(submitter)) ||
      (!submitter && (event.target.id === 'cart' || formHasCheckout))) blockCheckout(event);
  }, true);
  window.addEventListener('pageshow', event => {
    if (event.persisted && state.started) change(false, null);
  });
  let renderQueued = false;
  new MutationObserver(() => {
    if (!state.started || renderQueued) return;
    renderQueued = true;
    queueMicrotask(() => { renderQueued = false; render(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  class TermsCheckbox extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('#AgreeToTos') || this.querySelector('input[type="checkbox"]');
      if (!this.checkbox || state.widgets.has(this)) return;
      this.errorOutput = this.querySelector('[data-cgv-error-output]');
      if (!this.errorOutput) {
        this.errorOutput = document.createElement('p');
        this.errorOutput.dataset.cgvErrorOutput = '';
        this.errorOutput.setAttribute('role', 'alert');
        this.errorOutput.className = 'terms-checkbox__error';
        this.errorOutput.hidden = true;
        this.appendChild(this.errorOutput);
      }
      this.onConsentChange = () => change(this.checkbox.checked, this);
      this.checkbox.addEventListener('change', this.onConsentChange);
      state.widgets.add(this);
      if (!state.started) {
        state.started = true;
        change(false, null);
      } else if (state.desired.accepted) {
        try {
          if (!samePolicy(policyFor(this), state.desired.policy)) change(false, null);
        } catch (error) { change(false, null); }
      }
      render();
    }
    disconnectedCallback() {
      if (this.checkbox && this.onConsentChange) this.checkbox.removeEventListener('change', this.onConsentChange);
      state.widgets.delete(this);
      queueMicrotask(() => {
        if (state.started && !state.widgets.size && state.desired.accepted) change(false, null);
      });
    }
  }
  if (!customElements.get('terms-checkbox')) customElements.define('terms-checkbox', TermsCheckbox);
})();
